import { useCallback, useEffect, useState } from 'react'

function readStored(storageKey: string): string[] {
  try {
    const raw = window.localStorage.getItem(storageKey)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

/**
 * Expanded folder ids, persisted under `storageKey` so the tree survives a reload.
 * `alwaysExpanded` (the selected document's ancestor chain) is folded in on
 * change, so selecting a nested doc reveals it.
 */
export function useExpandedFolders(storageKey: string, alwaysExpanded: string[]) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(readStored(storageKey)))

  const expandAll = useCallback((ids: string[]) => {
    if (!ids.length) return
    setExpanded((prev) => {
      if (ids.every((id) => prev.has(id))) return prev
      return new Set([...prev, ...ids])
    })
  }, [])

  const key = alwaysExpanded.join(',')
  useEffect(() => {
    expandAll(key ? key.split(',') : [])
  }, [key, expandAll])

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...expanded]))
    } catch {
      // A full or blocked localStorage just costs us the persisted state.
    }
  }, [expanded, storageKey])

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })
  }, [])

  return { expanded, toggle, expandAll }
}
